import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Droplets,
  Fuel,
  Settings,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '../store';
import { CommoditiesTicker } from './CommoditiesTicker';
import { Jack2Button } from './Jack2Button';
import { Jack2Chat } from './Jack2Chat';
import { useAppStore } from '../store';

const NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/car-wash', icon: Droplets, label: 'Car Wash' },
  { path: '/gas-prices', icon: Fuel, label: 'Gas Prices' },
  { path: '/settings', icon: Settings, label: 'Settings' },
] as const;

export function Layout() {
  const location = useLocation();
  const { logout } = useAuthStore();
  const { jack2Open } = useAppStore();

  return (
    <div className="min-h-screen bg-bg-dark flex flex-col">
      {/* Commodities Ticker */}
      <CommoditiesTicker />

      {/* Header - Tall with Logo */}
      <header className="bg-bg-darker border-b border-text-muted/20">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/maplewood-auto-logo.png"
              alt="Maplewood Auto Service"
              className="w-16 h-16 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-text-primary tracking-tight flex items-center gap-2">
                <Droplets className="w-5 h-5 text-jrd-orange" />
                UNLIMITED WASH
              </h1>
              <p className="text-text-muted text-xs font-mono">
                featuring <span className="text-jrd-green">Maplewood Auto Service</span>
              </p>
            </div>
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 rounded text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-all text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Navigation Bar - Horizontal */}
      <nav className="bg-bg-darker/50 border-b border-text-muted/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1">
            {NAV_ITEMS.map(({ path, icon: Icon, label }) => {
              const isActive = path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(path);
              return (
                <NavLink
                  key={path}
                  to={path}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                    isActive
                      ? 'border-jrd-orange text-jrd-orange'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-text-muted/30'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Jack2 Floating Button */}
      <Jack2Button />

      {/* Jack2 Chat Panel */}
      {jack2Open && <Jack2Chat />}

      {/* Footer badge */}
      <div className="fixed bottom-2 right-2 text-text-muted text-[10px] font-mono pointer-events-none z-40">
        // crafted with rage by jack
      </div>
    </div>
  );
}
